import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { Box, HStack, Text } from "@chakra-ui/react"

export type BillingInterval = "month" | "year"

type Props = {
  value: BillingInterval
  onChange: (next: BillingInterval) => void
  /** Triggers thumb remeasure when changed (e.g. plans load). */
  layoutDependency?: unknown
}

const BillingIntervalToggle = ({ value, onChange, layoutDependency }: Props) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const monthTabRef = useRef<HTMLDivElement>(null)
  const yearTabRef = useRef<HTMLDivElement>(null)
  const [thumb, setThumb] = useState({ left: 0, width: 0 })

  const updateThumb = useCallback(() => {
    const track = trackRef.current
    const monthEl = monthTabRef.current
    const yearEl = yearTabRef.current
    if (!track || !monthEl || !yearEl) return

    const tabEl = value === "month" ? monthEl : yearEl

    let left: number
    let width: number
    if (tabEl.offsetParent === track) {
      left = tabEl.offsetLeft
      width = tabEl.offsetWidth
    } else {
      const trackRect = track.getBoundingClientRect()
      const tabRect = tabEl.getBoundingClientRect()
      const insetLeft = track.clientLeft
      left = tabRect.left - trackRect.left - insetLeft
      width = tabRect.width
    }

    setThumb({
      left: Math.round(left),
      width: Math.round(width),
    })
  }, [value])

  useLayoutEffect(() => {
    let cancelled = false
    let innerRaf = 0
    const outerRaf = requestAnimationFrame(() => {
      if (cancelled) return
      updateThumb()
      innerRaf = requestAnimationFrame(() => {
        if (!cancelled) updateThumb()
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(outerRaf)
      cancelAnimationFrame(innerRaf)
    }
  }, [updateThumb, layoutDependency])

  useEffect(() => {
    const track = trackRef.current
    if (!track) return undefined

    const ro = new ResizeObserver(() => { updateThumb() })
    ro.observe(track)
    window.addEventListener("resize", updateThumb)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", updateThumb)
    }
  }, [updateThumb, layoutDependency])

  useEffect(() => {
    let cancelled = false
    const syncThumbAfterFonts = async (): Promise<void> => {
      const { fonts } = document
      if (fonts?.ready) {
        await fonts.ready
      }
      if (!cancelled) updateThumb()
    }
    syncThumbAfterFonts().catch(() => undefined)
    return () => { cancelled = true }
  }, [updateThumb, layoutDependency])

  return (
    <Box
      ref={trackRef}
      role="tablist"
      aria-label="Billing period"
      position="relative"
      display="flex"
      flexDirection="row"
      alignItems="stretch"
      p={1.5}
      bg="gray.100"
      borderRadius="full"
      borderWidth={1}
      borderColor="gray.200"
      w="fit-content"
      maxW="100%"
    >
      <Box
        position="absolute"
        top={1.5}
        bottom={1.5}
        left={`${thumb.left}px`}
        w={`${thumb.width}px`}
        borderRadius="full"
        bg="white"
        boxShadow="sm"
        pointerEvents="none"
        zIndex={0}
        transitionProperty="left, width"
        transitionDuration="0.22s"
        transitionTimingFunction="ease-in-out"
      />
      <Box
        ref={monthTabRef}
        as="button"
        type="button"
        role="tab"
        aria-selected={value === "month"}
        onClick={() => { onChange("month") }}
        position="relative"
        zIndex={1}
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: 5, md: 8 }}
        py={3}
        minH={11}
        borderRadius="full"
        fontWeight={value === "month" ? "semibold" : "normal"}
        fontSize="lg"
        lineHeight="short"
        color="black"
        bg="transparent"
        transition="font-weight 0.12s ease"
        _hover={{
          fontWeight: value === "month" ? "semibold" : "medium",
        }}
      >
        Monthly
      </Box>
      <Box
        ref={yearTabRef}
        as="button"
        type="button"
        role="tab"
        aria-selected={value === "year"}
        onClick={() => { onChange("year") }}
        position="relative"
        zIndex={1}
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: 5, md: 8 }}
        py={3}
        minH={11}
        borderRadius="full"
        fontWeight={value === "year" ? "semibold" : "normal"}
        fontSize="lg"
        lineHeight="short"
        color="black"
        bg="transparent"
        transition="font-weight 0.12s ease"
        _hover={{
          fontWeight: value === "year" ? "semibold" : "medium",
        }}
      >
        <HStack spacing={2} alignItems="center">
          <Text as="span">Yearly</Text>
          <Text
            as="span"
            color={value === "year" ? "blue.500" : "black"}
            fontWeight={value === "year" ? "semibold" : "normal"}
            fontSize="md"
          >
            save 40%
          </Text>
        </HStack>
      </Box>
    </Box>
  )
}

export default BillingIntervalToggle
